import React, { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/dates';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './OnlineBook.css';

const emptyForm = (type = 'in') => ({
  id: null,
  type,
  amount: '',
  transaction_date: new Date().toISOString().slice(0, 10),
  category: '',
  notes: ''
});

const OnlineBook = () => {
  const [summary, setSummary] = useState({ balance: 0, total_in: 0, total_out: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm('in'));
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();

  const isEditing = Boolean(form.id);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const [summaryRes, listRes] = await Promise.all([
        axios.get('/api/online-book/summary'),
        axios.get('/api/online-book/transactions', { params })
      ]);
      setSummary({
        balance: Number(summaryRes.data.balance) || 0,
        total_in: Number(summaryRes.data.total_in) || 0,
        total_out: Number(summaryRes.data.total_out) || 0
      });
      setTransactions(listRes.data.transactions || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load Online Book');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = (type) => {
    setForm(emptyForm(type));
    setShowModal(true);
  };

  const openEdit = (tx) => {
    if (!tx.can_edit) return;
    setForm({
      id: tx.id,
      type: tx.type === 'out' ? 'out' : 'in',
      amount: String(tx.amount ?? ''),
      transaction_date: String(tx.transaction_date || '').slice(0, 10),
      category: tx.category || '',
      notes: tx.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setForm(emptyForm('in'));
  };

  const handleDelete = async (tx) => {
    if (!tx.can_delete) return;
    if (!window.confirm('Delete this Online Book entry?')) return;
    try {
      await axios.delete(`/api/online-book/transactions/${tx.id}`);
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete entry');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid amount greater than 0');
      return;
    }
    if (!String(form.category || '').trim()) {
      alert('Category / reason is required');
      return;
    }
    if (!form.transaction_date) {
      alert('Date is required');
      return;
    }

    const payload = {
      type: form.type === 'out' ? 'out' : 'in',
      amount,
      transaction_date: form.transaction_date,
      category: String(form.category).trim(),
      notes: String(form.notes || '').trim() || null
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`/api/online-book/transactions/${form.id}`, payload);
      } else {
        await axios.post('/api/online-book/transactions', payload);
      }
      setShowModal(false);
      setForm(emptyForm('in'));
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save Online Book entry');
    } finally {
      setSaving(false);
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="page-container">
        <div className="container">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container cash-page">
        <div className="page-header">
          <div>
            <h1>Online Book</h1>
            <p className="cash-subtitle">Add or spend online money. Card/bank/UPI invoice payments post here. Add/Spend entries can be edited or deleted.</p>
          </div>
          <div className="cash-actions">
            <button type="button" className="btn btn-primary" onClick={() => openAdd('in')}>
              Add
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => openAdd('out')}>
              Spend
            </button>
          </div>
        </div>

        <div className="cash-summary-grid">
          <div className="cash-summary-card cash-balance online-book-balance">
            <span className="cash-summary-label">Current balance</span>
            <strong>{formatCurrency(summary.balance)}</strong>
          </div>
          <div className="cash-summary-card cash-in">
            <span className="cash-summary-label">Total added</span>
            <strong>{formatCurrency(summary.total_in)}</strong>
          </div>
          <div className="cash-summary-card cash-out">
            <span className="cash-summary-label">Total spent</span>
            <strong>{formatCurrency(summary.total_out)}</strong>
          </div>
        </div>

        <div className="cash-filters">
          <div className="form-group">
            <label>From</label>
            <DateInput value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="form-group">
            <label>To</label>
            <DateInput value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => { setFrom(''); setTo(''); }}>
            Clear dates
          </button>
        </div>

        <div className="cash-ledger-wrap table-container">
          <table className="table cash-ledger-table">
            <thead>
              <tr>
                <th className="col-date">Date</th>
                <th className="col-type">Type</th>
                <th className="col-amount">Amount</th>
                <th className="col-category">Category</th>
                <th className="col-notes">Notes</th>
                <th className="col-by">By</th>
                <th className="col-balance">Running balance</th>
                <th className="col-logged">Logged</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>No Online Book entries yet</td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="col-date">{formatDate(tx.transaction_date)}</td>
                    <td className="col-type">
                      <span className={`cash-type-badge cash-type-${tx.type}`}>
                        {tx.type === 'in' ? 'Add' : 'Spend'}
                      </span>
                    </td>
                    <td className={`col-amount ${tx.type === 'out' ? 'cash-amount-out' : 'cash-amount-in'}`}>
                      {tx.type === 'out' ? '−' : '+'}
                      {formatCurrency(tx.amount)}
                    </td>
                    <td className="col-category">{tx.category || '—'}</td>
                    <td className="col-notes cash-notes-cell">{tx.notes || '—'}</td>
                    <td className="col-by">{tx.created_by_label || '—'}</td>
                    <td className="col-balance">{formatCurrency(tx.running_balance)}</td>
                    <td className="col-logged">{formatDateTime(tx.created_at)}</td>
                    <td className="col-actions">
                      {tx.can_edit ? (
                        <KebabMenu
                          menuId={`online-${tx.id}`}
                          openId={openActionMenu}
                          setOpenId={setOpenActionMenu}
                          items={[
                            { label: 'Edit', icon: MENU_ICONS.edit, onClick: () => openEdit(tx) },
                            { label: 'Delete', icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(tx) }
                          ]}
                        />
                      ) : (
                        <span className="cash-locked-hint">Invoice</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content cash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isEditing
                  ? (form.type === 'in' ? 'Edit add entry' : 'Edit spend entry')
                  : (form.type === 'in' ? 'Add online' : 'Spend online')}
              </h2>
              <button type="button" className="close-btn" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  <option value="in">Add</option>
                  <option value="out">Spend</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  required
                  autoFocus
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <DateInput
                  required
                  value={form.transaction_date}
                  onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Category / reason *</label>
                <input
                  type="text"
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder={form.type === 'in' ? 'e.g. Bank deposit, UPI received' : 'e.g. Online purchase, Transfer out'}
                />
              </div>
              <div className="form-group">
                <label>Details / notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional details for the audit trail"
                />
              </div>
              {form.type === 'out' && (
                <p className="cash-modal-hint">
                  Available online: {formatCurrency(summary.balance)}. Spend cannot exceed Online Book.
                </p>
              )}
              <div className="cash-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : isEditing ? 'Save changes' : (form.type === 'in' ? 'Add' : 'Spend')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineBook;
